/**
 * FEA-309 -- the sign-in flow's decisions, as pure functions.
 *
 * These used to live inline in the component, where nothing could test them: this repo has no React
 * testing library (CRA 3 does not ship one and `@testing-library/react` is not a dependency), so
 * anything inside a component is verified by clicking. The decisions are the part most likely to be
 * wrong -- which screen to show, which message, whether the challenge survives -- so they live here
 * instead and the component is left as wiring.
 */

export const CREDENTIALS = 'credentials'
export const PASSCODE = 'passcode'

/**
 * The message a rejected ApiClient promise should show.
 *
 * ApiClient rejects with superagent's error, which carries the parsed body. The API's own copy is
 * the right thing to show -- `admin_mfa_code_invalid` deliberately reads identically for a wrong
 * code, an expired challenge and an unknown one, so echoing it cannot reveal which it was.
 *
 * `fallback` is not dead code: a network error, a timeout or a CORS failure produces an error with
 * no `response` at all, and then it is the only message there is.
 */
export function messageFrom (error, fallback) {
  const body = (error && error.response && error.response.body) || {}
  return body.msg || fallback
}

/**
 * The transport status. Null when there was no response at all, which is how the caller tells a
 * dropped connection from a rejected request.
 *
 * Deliberately NOT the envelope's `code`: the API sets `ctx.status` and `code` from the same value,
 * so they can never disagree, and reading the envelope first would make this return null for the
 * responses that do not have one -- an ELB 502 while the workers are restarting, a Koa plain-text
 * 404, an HTML error page from in front of the app.
 */
export function statusFrom (error) {
  if (error && error.status) return error.status
  return null
}

/**
 * The status the APPLICATION reported, as opposed to whatever answered the socket. Null when the
 * response carried no `{ status, msg, code, data }` envelope, which is exactly how you tell "the API
 * said 502" from "something in front of the API said 502".
 *
 * That distinction is load-bearing: a 502 from the app means the Braze passcode send failed, and a
 * 502 from the load balancer means the request never arrived. Telling an admin we could not email
 * their passcode when nothing ever reached us sends them looking in the wrong place.
 */
export function envelopeCodeFrom (error) {
  const body = (error && error.response && error.response.body) || {}
  return body.code || null
}

/**
 * What a successful POST /admin/login means.
 *
 * Two shapes are legitimate: a token (admin_mfa_required is off -- the shipping default), or
 * `{ mfaRequired, challengeId }` (flag on). Anything else is a contract the client does not
 * understand, and saying so beats silently sitting on the sign-in screen doing nothing.
 */
export function nextStepFromLogin (response) {
  if (response && response.token) {
    return { step: CREDENTIALS, signIn: response }
  }
  if (response && response.mfaRequired && response.challengeId) {
    return { step: PASSCODE, challengeId: response.challengeId }
  }
  return { step: CREDENTIALS, error: 'We could not sign you in. Please try again.' }
}

/**
 * What a failed POST /admin/login means.
 *
 * 502 is the one that matters: Braze is the only passcode delivery path, so a failed send is a
 * failed login. Telling the admin their password was wrong would send them off to reset a password
 * that is fine, so it gets its own copy and names the real next step.
 */
export function loginFailureAction (status, serverMessage, envelopeCode) {
  // Only the APP's own 502 means the passcode send failed. A 502 with no envelope came from in front
  // of the app (the load balancer returns these for ~15s whenever the workers restart), and blaming
  // Braze for it sends the admin chasing a delivery problem that does not exist.
  if (status === 502 && envelopeCode === 502) {
    return {
      error: serverMessage || 'We could not email your passcode. Please try again in a moment, and tell engineering if it keeps failing.',
    }
  }
  if (status === null || status === undefined || envelopeCode === null) {
    // No response, or a response from something that is not the API.
    return { error: 'We could not reach the server right now. Please try again in a moment.' }
  }
  return { error: serverMessage || 'Incorrect email or password. Please try again.' }
}

/**
 * What a failed POST /admin/login/verify means.
 *
 * 429 is the attempt budget being spent: the server has destroyed the challenge, so there is nothing
 * left to retry on the passcode screen and leaving the admin there is a dead end. Everything else is
 * 401, which the API returns identically for a wrong code, an expired challenge and an unknown one.
 */
export function verifyFailureAction (status, serverMessage) {
  if (status === 429) {
    // Two different things produce a 429 here with the same envelope code: the 5-attempt budget
    // (which destroys the challenge server-side) and the route's 30/60s IP limiter (which does not).
    // They are only distinguishable by the message text, and branching on translated copy is the
    // thing this module exists to avoid -- so both are treated as start-over. That is the safe
    // direction: keeping a challenge the server has already destroyed leaves a dead screen, whereas
    // starting over after a shared-NAT rate limit merely costs one extra sign-in.
    return {
      step: CREDENTIALS,
      clearChallenge: true,
      error: serverMessage || 'Too many attempts. Please sign in again to get a new code.',
    }
  }
  if (status === null || status === undefined) {
    return { step: PASSCODE, clearChallenge: false, error: 'We could not reach the server. Check your connection and try again.' }
  }
  // Only a 401 actually means the code was wrong. A 400 (a malformed body), a 500, or a 502 from in
  // front of the app are all server problems, and telling the admin their code was wrong for any of
  // them sends them retyping a code that is fine.
  if (status !== 401) {
    return { step: PASSCODE, clearChallenge: false, error: 'Something went wrong on our side. Please try again in a moment.' }
  }
  return {
    step: PASSCODE,
    clearChallenge: false,
    error: serverMessage || 'That code did not work. Please try again.',
  }
}

/**
 * What a failed POST /admin/login/resend means.
 *
 * Note what is NOT here: a 401 branch. An expired, consumed or decoy challenge answers 200, not 401 --
 * the API cannot say "that challenge is gone" without also answering the question "is this address an
 * admin?". So an expired challenge never reaches this function at all, and the only place it can be
 * given a way out is the success copy (resendNotice below). An earlier version of this module did
 * branch on 401 here; it was unreachable, and the test covering it passed while the dead end stayed
 * open.
 *
 * 429 is the cooldown -- wait and retry, so the screen stays put.
 * 502 is the send failing, and the server destroys the challenge before rethrowing.
 */
export function resendFailureAction (status, serverMessage) {
  if (status === 429) {
    return {
      step: PASSCODE,
      clearChallenge: false,
      error: serverMessage || 'A code was just sent. Wait a moment before requesting another.',
    }
  }
  if (status === null || status === undefined) {
    return { step: PASSCODE, clearChallenge: false, error: 'We could not reach the server right now. Please try again in a moment.' }
  }
  // 502 specifically: the send failed and the server destroys the challenge before rethrowing, so
  // starting over is right -- and say why, rather than calling a delivery failure an expiry.
  if (status === 502) {
    return {
      step: CREDENTIALS,
      clearChallenge: true,
      error: serverMessage || 'We could not send another code. Please sign in again to start over.',
    }
  }
  // Anything else (a 400 from a malformed challengeId, a 500) is a server problem, not a delivery
  // failure. Keep the challenge -- the code the admin already has may still work.
  return {
    step: PASSCODE,
    clearChallenge: false,
    error: 'Something went wrong on our side. Please try again in a moment.',
  }
}

/**
 * The copy shown when a resend SUCCEEDS.
 *
 * It deliberately does not claim an email was sent, because a 200 here does not mean one was. The
 * API answers 200 for an expired or already-used challenge, and for the decoy challenge a non-admin
 * receives, precisely so that a resend cannot be used to ask "is this address an admin?" -- so
 * "We sent a new code" is a promise the client is in no position to make, and making it is what
 * turns an expired challenge into a loop: the admin is told to wait for mail that is not coming.
 *
 * Carrying its own escape hatch is the point. This is the only place the expired case can be given a
 * way out, because nothing in the response distinguishes it.
 */
export function resendNotice () {
  return 'If a new code does not arrive within a minute, sign in again to start over.'
}
